// ============================================================================
// MOVEMENT CAPTURE FORM
// ARQUIVO: src/features/files/MovementCaptureForm.jsx
// RESPONSABILIDADE: Captura manual de uma movimentação processual — texto
//   oficial colado do tribunal (imutável após criação, nunca editado por IA
//   "para ficar bonito") + metadados mínimos.
// Fusão Processos/Caderno (MC10): porte de
//   src/features/processes/MovementCaptureForm.jsx (branch pausada
//   feature/movimentacoes-processuais, MC04-05) — só troca processId por
//   workspaceId e a origem do addMovement (CadernoCockpitRepository em vez
//   de ProcessManagerRepository). Nenhum texto de guardrail foi alterado.
// ============================================================================
import React, { useState } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { addMovement } from './CadernoCockpitRepository';
import { SaveCancelBar } from '../../components/ui/SaveCancelBar';
import { db, storage } from '../../shared/api/firebase/config';
import { FS } from '../../shared/constants/firestorePaths';
import { FIREBASE_CONFIG } from '../../config/Config';
import { computeFileSHA256 } from '../../utils/cryptoUtils';

const TIPO_MOVIMENTO_OPTIONS = ['ANDAMENTO', 'SENTENCA', 'AUDIENCIA', 'ARQUIVADO'];
const appId = FIREBASE_CONFIG.projectId;

// Upload do PDF do andamento + registro em file_hub_attachments (FK própria,
// não reaproveita useFileHub.js — motor do Caderno inteiro, amarrado a
// workspace/peça do Drive, não upload genérico).
async function uploadMovementAttachment(workspaceId, file) {
  const sha256 = await computeFileSHA256(file);
  const storagePath = `movement_attachments/${workspaceId}/${Date.now()}_${file.name}`;
  const fileRef = storageRef(storage, storagePath);
  await uploadBytes(fileRef, file);
  const downloadURL = await getDownloadURL(fileRef);

  const attachmentsRef = collection(db, ...FS.fileHubAttachments(appId));
  const attachmentDoc = await addDoc(attachmentsRef, {
    workspaceId,
    nomeArquivo: file.name,
    sha256,
    storagePath,
    downloadURL,
    origem: 'MOVEMENT_CAPTURE',
    createdAt: serverTimestamp(),
  });

  return { fileHubAttachmentId: attachmentDoc.id, sha256, nomeArquivo: file.name };
}

export const MovementCaptureForm = ({ workspaceId, numeroCNJ, pieces = [], onClose, onSaved }) => {
  const [form, setForm] = useState({
    descricaoOficial: '',
    dataMovimento: new Date().toISOString().slice(0, 10),
    tipoMovimento: 'ANDAMENTO',
    numeroMovimento: '',
    usuario: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  // Vínculo manual Histórico↔Motor de Anexos (aprovado 22/08/2026, Opção 2):
  // não existe ID compartilhado entre file_hub_attachments e as peças do
  // Motor de Anexos (Google Drive) — em vez de tentar casar por hash (fora
  // de escopo), o titular declara manualmente "este anexo também é a peça X".
  const [vinculoPecaId, setVinculoPecaId] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.descricaoOficial.trim()) {
      alert('Cole o texto oficial da movimentação antes de salvar.');
      return;
    }
    setSaving(true);
    try {
      const pecaVinculada = vinculoPecaId ? pieces.find((p) => String(p?.id) === vinculoPecaId) : null;
      const anexoBase = file ? await uploadMovementAttachment(workspaceId, file) : null;
      const anexo = anexoBase && pecaVinculada
        ? { ...anexoBase, pieceLabel: pecaVinculada.pieceType || pecaVinculada.processPieceFolderName || null }
        : anexoBase;
      await addMovement(workspaceId, {
        numeroCNJ: numeroCNJ || null,
        numeroMovimento: form.numeroMovimento.trim() || null,
        dataMovimento: `${form.dataMovimento}T12:00:00-03:00`,
        dataCaptura: new Date().toISOString(),
        codigoCNJ: null,
        tipoMovimento: form.tipoMovimento,
        descricaoOficial: form.descricaoOficial.trim(),
        usuario: form.usuario.trim() || null,
        resumo: null,
        anexo,
        pieceId: null,
        origem: 'MANUAL',
      });
      onSaved?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Erro ao salvar a movimentação.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border-t-4 border-brand-blue">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800">
            <i className="fas fa-file-signature text-brand-blue mr-2"></i>Nova Movimentação
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500"><i className="fas fa-times"></i></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Data do Movimento</label>
            <input type="date" name="dataMovimento" value={form.dataMovimento} onChange={handleChange} className="w-full p-2 border rounded-lg text-sm focus:border-brand-blue outline-none" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Tipo</label>
            <select name="tipoMovimento" value={form.tipoMovimento} onChange={handleChange} className="w-full p-2 border rounded-lg text-sm bg-white focus:border-brand-blue outline-none">
              {TIPO_MOVIMENTO_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Nº no Tribunal (opcional)</label>
            <input
              type="text"
              name="numeroMovimento"
              value={form.numeroMovimento}
              onChange={handleChange}
              placeholder="ex.: 84"
              className="w-full p-2 border rounded-lg text-sm focus:border-brand-blue outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Usuário no Tribunal (opcional)</label>
            <input
              type="text"
              name="usuario"
              value={form.usuario}
              onChange={handleChange}
              placeholder="quem praticou o ato (ex.: nome do advogado, SISTEMA CNJ)"
              className="w-full p-2 border rounded-lg text-sm focus:border-brand-blue outline-none"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
              Descrição Oficial (colar do site do tribunal, texto literal)
            </label>
            <textarea
              name="descricaoOficial"
              value={form.descricaoOficial}
              onChange={handleChange}
              rows={8}
              placeholder="Cole aqui o andamento/decisão exatamente como aparece no Projudi/TJGO..."
              className="w-full p-2 border rounded-lg text-sm focus:border-brand-blue outline-none font-mono"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Este campo fica imutável após salvar — se o tribunal republicar o mesmo ato, crie uma movimentação nova, nunca edite esta.
            </p>
          </div>
          <div className="col-span-2">
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">PDF do Andamento (opcional)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm p-2 border rounded-lg bg-white file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-brand-blue file:text-white file:text-xs file:font-bold"
            />
            {file && <p className="text-[10px] text-gray-500 mt-1"><i className="fas fa-paperclip mr-1"></i>{file.name}</p>}
          </div>
          {file && pieces.length > 0 && (
            <div className="col-span-2">
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                Este anexo também está na peça (opcional)
              </label>
              <select
                value={vinculoPecaId}
                onChange={(e) => setVinculoPecaId(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm bg-white focus:border-brand-blue outline-none"
              >
                <option value="">(Não vincular a nenhuma peça)</option>
                {pieces.map((p) => (
                  <option key={p.id} value={p.id}>{p.pieceType || p.processPieceFolderName || p.id}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">
                Vínculo declarado manualmente — não há verificação automática de que é o mesmo arquivo.
              </p>
            </div>
          )}
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          <SaveCancelBar
            onCancel={onClose}
            onSave={handleSave}
            cancelLabel="Cancelar"
            saveLabel={saving ? 'Salvando...' : 'Salvar'}
          />
        </div>
      </div>
    </div>
  );
};
